import React, { useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import ConfirmationDialog from '@ts-core/components/ConfirmationDialog';
import ToMainMenuButton from '@ts-core/components/ToMainMenuButton';
import PageContentLayout from '@ts-core/core/components/PageContentLayout';
import { useSectionedAssignments } from '@ts-core/hooks/assignment';

import AssignmentsList from '../../components/AssignmentsList';
import { Assignment } from '../../types';
import { useAssignments } from '../../hooks';

const AssignmentsListPage = () => {
  const history = useHistory();
  const { courseId } = useParams<{ courseId: string }>();
  const [assignmentToStart, setAssignmentToStart] = useState<Assignment>();

  const { assignments, loading } = useAssignments({ courseId });
  const { nextAssignments, pastAssignments } = useSectionedAssignments({
    assignments,
  });

  const handleOpenAssignmentDetails = useCallback(
    (assignment: Assignment) => {
      history.push(`assignments/${assignment.id}/details`);
    },
    [history]
  );

  const handleStartAssignment = useCallback(
    (assignment: Assignment) => {
      history.push(`assignments/${assignment.id}/do`);
    },
    [history]
  );

  const handleSelectAssignment = useCallback(
    (assignment: Assignment) => {
      const { answered, answer, expired, timeLimit } = assignment;
      const { startedAt } = answer || {};
      if (answered) {
        handleOpenAssignmentDetails(assignment);
      } else if (!expired) {
        if (timeLimit && !startedAt) {
          setAssignmentToStart(assignment);
        } else {
          handleStartAssignment(assignment);
        }
      }
    },
    [handleOpenAssignmentDetails, handleStartAssignment]
  );

  const handleCancelStartAssignment = useCallback(() => {
    setAssignmentToStart(undefined);
  }, [setAssignmentToStart]);

  const handleConfirmStartAssignment = useCallback(() => {
    if (assignmentToStart) {
      handleStartAssignment(assignmentToStart);
    }
  }, [handleStartAssignment, assignmentToStart]);

  return (
    <>
      <ToMainMenuButton title={<FormattedMessage id="assignments" />} />
      <PageContentLayout>
        <AssignmentsList
          nextAssignments={nextAssignments}
          pastAssignments={pastAssignments}
          loading={loading}
          onSelectAssignment={handleSelectAssignment}
        />
      </PageContentLayout>
      <ConfirmationDialog
        open={Boolean(assignmentToStart)}
        title={<FormattedMessage id="studentAssignment.withTimeLimitAlert.title" />}
        message={
          <FormattedMessage
            id="studentAssignment.withTimeLimitAlert.message"
            values={{
              minutes: assignmentToStart ? assignmentToStart.timeLimit : 0,
            }}
          />
        }
        confirmActionTitle={<FormattedMessage id="button.start" />}
        cancelActionTitle={<FormattedMessage id="button.cancel" />}
        onCancel={handleCancelStartAssignment}
        onConfirm={handleConfirmStartAssignment}
      />
    </>
  );
};

export default AssignmentsListPage;
